// Hint adapter over the official 2026 card. Ranks the card hands by how well
// the viewer's tiles fit them and explains what's still needed. Pure — backed
// by handMatch.js + the NMJL 2026 data. (Replaces the old fictional-pattern
// fit heuristic; see DECISIONS D-005 revisited 2026-06-16.)

import { handFit, bestHands } from './handMatch.js';
import { HANDS } from './cards/nmjl2026.js';
import { tileKey, tileName } from './tiles.js';

// Top card hands for these tiles, best fit first. Each entry exposes the real
// hand plus display fields and the fit's needed-tile map.
export function rankTargets(hand, hasExposures = false, min = 4) {
  return HANDS
    .map((h) => {
      const fit = handFit(hand, h, hasExposures);
      return {
        hand: h, section: h.section, label: h.label, desc: h.desc,
        value: h.value, concealed: h.concealed, score: fit.score, need: fit.need,
      };
    })
    .filter((t) => t.score >= min)
    .sort((a, b) => b.score - a.score);
}

// The single best-fitting hand (or null).
export function findBestTarget(hand, hasExposures = false) {
  const ranked = bestHands(hand, hasExposures);
  return ranked.length && ranked[0].score > 3 ? ranked[0] : null;
}

// Readable name for a tileKey, e.g. 'crak5' → '5 Crak', 'dragon_red' → 'Red Dragon'.
function keyToName(k) {
  if (k === 'flower') return 'Flower';
  if (k === 'joker') return 'Joker';
  if (k === 'dragon_white') return '0 / White Dragon';
  if (k.startsWith('dragon_')) return k.slice(7)[0].toUpperCase() + k.slice(8) + ' Dragon';
  if (k.startsWith('wind_')) return k.slice(5)[0].toUpperCase() + k.slice(6) + ' Wind';
  const m = /^(crak|bam|dot)(\d)$/.exec(k);
  if (m) return `${m[2]} ${m[1][0].toUpperCase() + m[1].slice(1)}`;
  return k;
}

// Plain-English "what you still need" for a target given the viewer's hand.
export function targetNeeds(target, hand) {
  const have = new Map();
  for (const t of hand) have.set(tileKey(t), (have.get(tileKey(t)) || 0) + 1);
  const jokers = hand.filter((t) => t.type === 'joker').length;
  const missing = [];
  for (const [k, n] of target.need) {
    const short = n - Math.min(have.get(k) || 0, n);
    if (short > 0) missing.push(`${short}× ${keyToName(k)}`);
  }
  if (!missing.length) return 'complete — call Mahjong!';
  const head = missing.slice(0, 6).join(', ');
  return jokers ? `${head} (you hold ${jokers} joker${jokers > 1 ? 's' : ''})` : head;
}

export { HANDS, tileName };
